import React, { useEffect, useMemo, useState } from 'react'
import { motion } from 'motion/react'
import {
  ProjectSummary,
  SummaryConfig,
  SummaryResultResponse,
  SummaryTier,
  getSummaryConfigs,
  getSummaryResult,
} from '../../api/client'
import SummaryConfigMenu from './SummaryConfigMenu'

/**
 * ProjectInsightsCard — the project-page Insights surface (Stage 7).
 *
 * Replaces the inline Insights block on ProjectDetailPage. It is a NON-
 * destructive cutover: the legacy `project_summaries` themes / lessons /
 * squad_thoughts still arrive via the existing `getProjectSummary` call and are
 * passed in as the `legacy` prop, so ThemeTabs keeps rendering exactly as
 * before. On top of that, this card layers the NEW system's data:
 *
 *  - It looks up the per-project `project_narrative` `summary_config` (the one
 *    the backend bootstrap provisions, pinned to the project page) and fetches
 *    that config's latest precomputed `summary_results` row at the selected
 *    tier via the strictly read-only `GET /api/summaries/results`.
 *  - When the row carries a `text_summary`, it renders that narrative.
 *  - When the config has multiple tiers, a tier toggle lets the user switch.
 *
 * Stage 8 will rewrite `/api/projects/{id}/summary` to read from the new system
 * too and drop the legacy source; until then the two coexist.
 */

const ThemeTabs: React.FC<{ themes: { short: string; full: string }[] }> = ({ themes }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  if (themes.length === 0) return null
  const idx = Math.min(activeIndex, themes.length - 1)
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {themes.map((theme, index) => (
          <button
            key={index}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              idx === index
                ? 'bg-action text-plum'
                : 'bg-amber-100 hover:bg-amber-200 text-amber-900'
            }`}
            onClick={() => setActiveIndex(index)}
          >
            {theme.short}
          </button>
        ))}
      </div>
      <div className="bg-amber-50 border border-amber-200 p-3 rounded-md">
        <div className="text-sm text-amber-900">{themes[idx].full}</div>
      </div>
    </div>
  )
}

const TIER_ORDER: SummaryTier[] = ['week', 'month', 'year']
const TIER_LABEL: Record<SummaryTier, string> = {
  week: 'Week',
  month: 'Month',
  year: 'Year',
}

function todayIso(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Order a config's tiers canonically (week → month → year), ignoring junk. */
function orderedTiers(config: SummaryConfig | null): SummaryTier[] {
  if (!config) return []
  const present = new Set(config.tiers || [])
  return TIER_ORDER.filter((t) => present.has(t))
}

/** Pick the default tier to show: prefer month, else the first available. */
function defaultTier(tiers: SummaryTier[]): SummaryTier {
  if (tiers.includes('month')) return 'month'
  return tiers[0] ?? 'month'
}

interface ProjectInsightsCardProps {
  projectId: number
  /** Legacy `project_summaries` data (themes/lessons/squad_thoughts). Kept as
   *  the source of truth for those sections until Stage 8 migrates them. */
  legacy: ProjectSummary | null
}

const ProjectInsightsCard: React.FC<ProjectInsightsCardProps> = ({
  projectId,
  legacy,
}) => {
  const [config, setConfig] = useState<SummaryConfig | null>(null)
  const [configLoaded, setConfigLoaded] = useState(false)
  // Bumped after an edit / archive via the settings gear so the config (and its
  // narrative) re-resolve.
  const [version, setVersion] = useState(0)
  const tiers = useMemo(() => orderedTiers(config), [config])
  const [tier, setTier] = useState<SummaryTier>('month')
  const [result, setResult] = useState<SummaryResultResponse | null>(null)
  const [resultLoading, setResultLoading] = useState(false)

  // Resolve this project's project_narrative config by source membership (the
  // project page loads summaries by source, not by pin — see the Stage 17
  // load model).
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const configs = await getSummaryConfigs()
        if (cancelled) return
        const match =
          configs.find(
            (c) =>
              c.kind === 'project_narrative' &&
              c.status === 'active' &&
              Array.isArray(c.source_project_ids) &&
              c.source_project_ids.includes(projectId),
          ) ?? null
        setConfig(match)
        setTier(defaultTier(orderedTiers(match)))
      } catch (e) {
        if (cancelled) return
        console.error('Failed to load project summary configs', e)
        setConfig(null)
      } finally {
        if (!cancelled) setConfigLoaded(true)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [projectId, version])

  // Fetch the latest precomputed result for the selected tier (read-only).
  useEffect(() => {
    if (!config) {
      setResult(null)
      return
    }
    let cancelled = false
    const periodStart = todayIso()
    const load = async () => {
      setResultLoading(true)
      try {
        const res = await getSummaryResult({
          configId: config.id,
          tier,
          periodStart,
        })
        if (!cancelled) setResult(res)
      } catch (e) {
        if (cancelled) return
        console.error('Failed to load project summary result', e)
        setResult(null)
      } finally {
        if (!cancelled) setResultLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [config, tier])

  const themes = legacy?.themes ?? []
  const lessons = legacy?.lessons ?? []
  const squadThoughts = legacy?.squad_thoughts ?? []
  const narrative =
    result?.exists === true && result.text_summary ? result.text_summary : null

  const legacyEmpty =
    themes.length === 0 && lessons.length === 0 && squadThoughts.length === 0
  const everythingEmpty = legacyEmpty && !narrative

  // Render nothing at all only when there's neither a config nor any legacy
  // data — mirrors the old `projectSummary ? ... : null` behavior so a project
  // with no insights doesn't show an empty card.
  if (configLoaded && !config && !legacy) return null

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      style={{ transformOrigin: 'top center' }}
      className="bg-white p-6 sm:rounded-lg sm:shadow-md mb-8"
      data-testid="project-insights-card"
    >
      <div className="flex items-center justify-between mb-4 gap-3">
        <h2 className="text-2xl font-semibold text-gray-800">Insights</h2>

        <div className="flex items-center gap-2">
          {/* Tier toggle — only when the config offers more than one tier. */}
          {tiers.length > 1 && (
            <div className="flex gap-1.5" role="tablist" aria-label="Summary tier">
              {tiers.map((t) => (
                <button
                  key={t}
                  role="tab"
                  aria-selected={t === tier}
                  onClick={() => setTier(t)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    t === tier
                      ? 'bg-action text-plum'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {TIER_LABEL[t]}
                </button>
              ))}
            </div>
          )}

          {/* Settings gear — Edit / Archive this project's narrative config. */}
          {config && (
            <SummaryConfigMenu
              config={config}
              surface="project"
              projectId={projectId}
              onChanged={() => setVersion((v) => v + 1)}
            />
          )}
        </div>
      </div>

      {/* Themes (legacy source, ThemeTabs preserved) */}
      {themes.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Themes</h4>
          <ThemeTabs themes={themes} />
        </div>
      )}

      {/* Lessons (legacy source) */}
      {lessons.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Lessons</h4>
          <div className="space-y-2">
            {lessons.map((lesson, index) => (
              <div key={index} className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                {lesson}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Squad thoughts (legacy source) */}
      {squadThoughts.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">From Squad</h4>
          <div className="space-y-2">
            {squadThoughts.map((thought, index) => (
              <div key={index} className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                {thought}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Narrative (NEW system: project_narrative summary_results.text_summary) */}
      {narrative && (
        <div className="mb-0">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Narrative</h4>
          <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">
            {narrative}
          </p>
        </div>
      )}

      {/* Empty state */}
      {everythingEmpty && !resultLoading && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-sm">
            No insights available yet. Add some check-ins to generate insights!
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default ProjectInsightsCard
